import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Heart, Camera, Gamepad2, Plane, Utensils, Sparkles, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

export const CategoriesSection = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        const { data, error } = await supabase
          .from('influencers')
          .select('category')
          .eq('approval_status', 'approved');

        if (error) throw error;

        const counts: Record<string, number> = {};
        (data || []).forEach((inf) => {
          const cat = inf.category.toLowerCase();
          counts[cat] = (counts[cat] || 0) + 1;
        });
        setCategoryCounts(counts);
      } catch (error) {
        console.error('Error fetching category counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryCounts();
  }, []);

  const getCategoryCount = (categoryName: string) => {
    const searchTerms = categoryName.toLowerCase().split(/[&\s]+/).filter(Boolean);
    let total = 0;
    Object.entries(categoryCounts).forEach(([cat, count]) => {
      const catLower = cat.toLowerCase();
      if (searchTerms.some(term => catLower.includes(term))) {
        total += count;
      }
    });
    return total;
  };

  const categories = [
    {
      icon: Heart,
      name: language === 'ar' ? 'الجمال والموضة' : 'Beauty & Fashion',
      description: language === 'ar' ? 'خبراء التجميل والموضة' : 'Beauty and fashion experts',
      searchKey: 'beauty fashion'
    },
    {
      icon: Camera,
      name: language === 'ar' ? 'نمط الحياة' : 'Lifestyle',
      description: language === 'ar' ? 'محتوى نمط الحياة اليومية' : 'Daily lifestyle content',
      searchKey: 'lifestyle'
    },
    {
      icon: Gamepad2,
      name: language === 'ar' ? 'الألعاب والتقنية' : 'Gaming & Tech',
      description: language === 'ar' ? 'مراجعات الألعاب والتقنية' : 'Gaming and tech reviews',
      searchKey: 'gaming tech technology'
    },
    {
      icon: Plane,
      name: language === 'ar' ? 'السفر والمغامرة' : 'Travel & Adventure',
      description: language === 'ar' ? 'مغامرات السفر والاستكشاف' : 'Travel adventures and exploration',
      searchKey: 'travel adventure'
    },
    {
      icon: Utensils,
      name: language === 'ar' ? 'الطعام والطبخ' : 'Food & Cooking',
      description: language === 'ar' ? 'وصفات وتجارب طعام' : 'Recipes and food experiences',
      searchKey: 'food cooking culinary'
    },
    {
      icon: Sparkles,
      name: language === 'ar' ? 'الترفيه' : 'Entertainment',
      description: language === 'ar' ? 'محتوى ترفيهي متنوع' : 'Diverse entertainment content',
      searchKey: 'entertainment'
    }
  ];

  return (
    <div className="mb-24">
      <div className="text-center mb-8 sm:mb-16">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
          {t('categories')}
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto text-balance">

          {language === 'ar' 
            ? 'اكتشف المؤثرين في مختلف التخصصات والفئات'
            : 'Discover influencers across various specialties and categories'}
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((category, index) => {
          const IconComponent = category.icon;
          const count = getCategoryCount(category.searchKey);
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card 
                className="group relative overflow-hidden border border-border/30 backdrop-blur-sm bg-gradient-to-br from-background/90 via-background/80 dark:from-card/90 dark:via-card/80 to-primary/5 shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-500 hover:-translate-y-2"
                onClick={() => navigate('/influencer-booking')}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <CardHeader className="relative z-10 text-center p-4 sm:p-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-primary to-secondary rounded-2xl flex items-center justify-center mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-white w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <CardTitle className="text-base sm:text-xl text-foreground group-hover:text-primary transition-colors duration-300 text-balance">
                    {category.name}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="text-center relative z-10 p-4 pt-0 sm:p-6 sm:pt-0">
                  <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4 leading-relaxed">
                    {category.description}
                  </p>
                  <div className="inline-flex items-center bg-primary/10 text-primary px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">

                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        {count} {language === 'ar' ? 'مؤثر' : 'Influencers'}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
      
      <div className="text-center mt-12">
        <Button 
          size="lg"
          onClick={() => navigate('/influencer-booking')}
          className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          {t('viewInfluencers')}
        </Button>
      </div>
    </div>
  );
};
